import { type CategoryKind, formatEUR, resolveCycle } from '@budget/shared';
import { useState } from 'react';
import {
  useAccounts,
  useCreateRecurringItem,
  useDeleteRecurringItem,
  useRecurringItems,
  useSettings,
} from '../api/hooks';
import { AmountField } from '../components/AmountField';
import { BottomSheet } from '../components/BottomSheet';
import { useCreationPanel } from '../components/useCreationPanel';

function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * Revenus et charges fixes : c'est ce qui donne sa valeur au budget variable.
 * Sans eux, la courbe partirait de zéro et l'application ne dirait rien.
 */
export function FixedScreen() {
  const items = useRecurringItems();
  const accounts = useAccounts();
  const settings = useSettings();
  const create = useCreateRecurringItem();
  const remove = useDeleteRecurringItem();

  const [open, setOpen] = useCreationPanel();
  const [kind, setKind] = useState<CategoryKind>('expense');
  const [label, setLabel] = useState('');
  const [amountCents, setAmountCents] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const account = (accounts.data ?? []).find((item) => item.type === 'checking');
  const rows = items.data ?? [];
  const incomes = rows.filter((row) => row.kind === 'income');
  const expenses = rows.filter((row) => row.kind === 'expense');

  const monthlyIncome = incomes.reduce((sum, row) => sum + row.amountCents, 0);
  const monthlyExpense = expenses.reduce((sum, row) => sum + row.amountCents, 0);

  async function submit() {
    if (account === undefined || label.trim() === '' || amountCents <= 0) return;

    // Une ligne fixe décrit ce qui EXISTE DÉJÀ, pas ce qui démarrera plus tard.
    // La faire commencer aujourd'hui écarterait l'échéance du mois en cours dès
    // qu'elle est passée — un loyer prélevé le 1er, saisi le 15, disparaîtrait
    // du budget et la courbe tomberait à zéro.
    const cycle = resolveCycle(localToday(), settings.data?.cycleStartDay ?? 1);

    await create.mutateAsync({
      accountId: account.id,
      label: label.trim(),
      kind,
      amountCents,
      dayOfMonth,
      startOn: cycle.start,
    });
    setLabel('');
    setAmountCents(0);
    setOpen(false);
  }

  function section(title: string, list: typeof rows) {
    if (list.length === 0) return null;
    return (
      <section className="mt-5">
        <h2 className="text-xs font-medium text-ink-muted uppercase">{title}</h2>
        <ul className="mt-2 overflow-hidden rounded-2xl border border-line bg-surface">
          {list.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{row.label}</p>
                <p className="text-xs text-ink-muted">le {row.dayOfMonth} du mois</p>
              </div>
              <span className="tabular text-sm font-medium">{formatEUR(row.amountCents)}</span>
              <button
                type="button"
                onClick={() => remove.mutate(row.id)}
                aria-label={`Supprimer ${row.label}`}
                className="rounded-lg px-2 py-1 text-ink-muted active:bg-line"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <div className="px-5 pt-4">
      <h1 className="text-xl font-semibold">Revenus et charges fixes</h1>
      <p className="mt-1 text-sm text-ink-secondary">
        {formatEUR(monthlyIncome, { round: true })} de revenus,{' '}
        {formatEUR(monthlyExpense, { round: true })} de charges par mois.
      </p>

      {account === undefined && (
        <p className="mt-4 rounded-xl border border-line bg-surface p-4 text-sm text-ink-secondary">
          Créez d’abord un compte courant : c’est lui qui porte ces échéances.
        </p>
      )}

      {section('Revenus', incomes)}
      {section('Charges', expenses)}

      {open && (
        <BottomSheet
          label={kind === 'expense' ? 'Nouvelle charge fixe' : 'Nouveau revenu fixe'}
          onClose={() => setOpen(false)}
        >
          <div className="mt-4 flex flex-col gap-4">
            {/* Le bouton d'action ouvre toujours ce formulaire : c'est ici, et
              non sur un bouton grisé, qu'il faut expliquer ce qui manque. */}
            {account === undefined && (
              <p className="rounded-xl border border-line bg-plane p-3 text-sm text-ink-secondary">
                Créez d’abord un compte courant : c’est lui qui porte ces échéances.
              </p>
            )}

            <div className="flex rounded-lg border border-line p-0.5 text-sm">
              {(['expense', 'income'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setKind(option)}
                  className={`flex-1 rounded-md px-3 py-2 ${
                    kind === option ? 'bg-series text-white' : 'text-ink-secondary'
                  }`}
                >
                  {option === 'expense' ? 'Charge' : 'Revenu'}
                </button>
              ))}
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-ink-secondary">Libellé</span>
              <input
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder={kind === 'expense' ? 'Loyer' : 'Salaire'}
                className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
              />
            </label>

            <AmountField
              label="Montant mensuel"
              valueCents={amountCents}
              onChange={setAmountCents}
            />

            <label className="flex flex-col gap-1.5">
              <span className="text-sm text-ink-secondary">Jour du mois</span>
              <input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(event) => setDayOfMonth(Number(event.target.value))}
                className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
              />
              <span className="text-xs text-ink-muted">
                Le 29, 30 ou 31 est ramené au dernier jour des mois plus courts.
              </span>
            </label>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 rounded-xl border border-line py-3 text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={create.isPending || account === undefined}
                onClick={() => void submit()}
                className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
              >
                Ajouter
              </button>
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}
